/**
 * The stored graph layout mode, narrowed to what a canvas surface can draw.
 *
 * One preference for every graph, so it can name `force` — a mode that needs the
 * optional heavy engine this app does not ship. `resolveGraphViewMode` is what
 * keeps that from reaching a canvas; the pref itself is shared, so the narrowing
 * belongs with it rather than at each reader.
 *
 * In its own module, away from `<FeedGraph>`, because of what it costs to
 * import. A host that lets a saved view snapshot the mode has to lift it ABOVE
 * the graph, and the graph is lazily loaded precisely so `@xyflow/react` and the
 * layout engines stay out of the eager chunk — so a pref hook living in the
 * canvas's file made every such host pull React Flow to read a string. That is
 * how the overview's activity panel, whose default view is the TABLE, came to
 * carry the whole graph engine in the landing chunk.
 *
 * It needs React, the display vocabulary and the prefs store, and nothing else.
 */
import { useDisplayPref } from "@authe/ui-core";
import {
  GRAPH_FLOW_VIEW_MODES,
  resolveGraphViewMode,
  type GraphViewMode,
} from "@authe/fe-commons/display";
import { displayPrefs } from "@/app/prefs";

export function useGraphViewModePref(): [GraphViewMode, (mode: GraphViewMode) => void] {
  const [stored, setMode] = useDisplayPref<GraphViewMode>(
    () => displayPrefs.getGraphViewMode(),
    (m) => displayPrefs.setGraphViewMode(m),
  );
  return [resolveGraphViewMode(stored, GRAPH_FLOW_VIEW_MODES), setMode];
}
